import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Container, Card, Modal, Accordion } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import { useBackgroundProjects } from "@hooks/useBackgroundData";
import { getEsgResourceEfficiency } from "../services/esgCrud";
import { getAllIrrigationAreas } from "@modules/IrrigationArea/services/areaCrud";
import EsgKpiCard from "../components/EsgKpiCard";
import EsgResourceEfficiencyChart from "../components/EsgResourceEfficiencyChart";
import "./EsgMonitoring.scss";

const CALC_BASIS_KEYS = [
  "esg.calc_volume_source",
  "esg.calc_baseline_profile",
  "esg.calc_seasonal_weighting",
  "esg.calc_water_saved",
  "esg.calc_co2_explanation",
  "esg.calc_co2_formula",
];

const ASSUMPTION_KEYS = [
  "esg.assumption_young_0_1",
  "esg.assumption_young_2_3",
  "esg.assumption_years_4_7",
  "esg.assumption_established",
  "esg.assumption_energy_intensity",
  "esg.assumption_emission_factor",
  "esg.assumption_seasonal",
];

const EXCLUSION_KEYS = [
  "esg.exclusion_missing_planting_year",
  "esg.exclusion_large_transplant",
  "esg.exclusion_no_volume",
];

// Extract a dynamic count from the first exclusion string if it starts with a number
function parseExclusionCount(exclusions) {
  if (!Array.isArray(exclusions) || exclusions.length === 0) return null;
  const match = exclusions[0]?.match(/^(\d+)\s/);
  return match ? parseInt(match[1], 10) : null;
}

export default function EsgMonitoring() {
  const { t, i18n } = useTranslation();
  const { selectedProjectId } = useProjectFilter();
  const { projects } = useBackgroundProjects();

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const [year, setYear] = useState(currentYear);
  const [areaId, setAreaId] = useState(null);
  const [showMethodology, setShowMethodology] = useState(false);

  const isWithoutProjectScope = selectedProjectId === "without-project";

  const effectiveProjectId =
    typeof selectedProjectId === "number" && Number.isFinite(selectedProjectId)
      ? selectedProjectId
      : null;

  useEffect(() => {
    setAreaId(null);
  }, [selectedProjectId]);

  const {
    data: esgData,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ["esg-resource-efficiency", { year, projectId: effectiveProjectId, areaId }],
    () =>
      getEsgResourceEfficiency({
        year,
        project_id: effectiveProjectId,
        area_id: areaId,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: false,
      enabled: !isWithoutProjectScope,
    }
  );

  const {
    data: allAreas,
    isLoading: isAreasLoading,
    isError: isAreasError,
  } = useQuery(["esg-areas"], () => getAllIrrigationAreas(), {
    enabled: effectiveProjectId != null && !isWithoutProjectScope,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const projectAreas = useMemo(() => {
    if (!Array.isArray(allAreas) || effectiveProjectId == null) return [];
    return allAreas.filter((area) => Number(area.project_id) === Number(effectiveProjectId));
  }, [allAreas, effectiveProjectId]);

  const formatNumber = (value, options = {}) => {
    if (value === null || value === undefined || Number.isNaN(value)) return "–";
    return new Intl.NumberFormat(i18n.language, options).format(value);
  };

  const summary = esgData?.summary ?? {};
  const chart = esgData?.chart ?? {};
  const methodology = esgData?.methodology ?? {};
  const quality = esgData?.quality ?? {};
  const scope = esgData?.scope ?? {};

  const exclusionCount = useMemo(
    () => parseExclusionCount(methodology.exclusions),
    [methodology.exclusions]
  );

  const projectLabel = useMemo(() => {
    if (selectedProjectId === "without-project") {
      return t("without_project");
    }
    if (effectiveProjectId === null) {
      return t("esg.all_projects");
    }
    const project = (projects || []).find((item) => Number(item.id) === effectiveProjectId);
    return (
      project?.name ||
      (scope.project_id === effectiveProjectId ? scope.project_label : null) ||
      t("esg.all_projects")
    );
  }, [selectedProjectId, effectiveProjectId, projects, scope.project_id, scope.project_label, t]);

  const areaLabel = useMemo(() => {
    if (areaId === null) return t("esg.all_areas");
    const selectedArea = projectAreas.find((area) => Number(area.id) === Number(areaId));
    if (selectedArea?.name) return selectedArea.name;
    if (Number(scope.area_id) === Number(areaId) && scope.area_label) {
      return scope.area_label;
    }
    return t("esg.all_areas");
  }, [areaId, projectAreas, scope.area_id, scope.area_label, t]);

  const isCurrentYear = year === currentYear;

  const chartSubtitle = useMemo(() => {
    if (isCurrentYear) {
      return t("esg.monthly_comparison_through_current_month");
    }
    return t("esg.monthly_comparison");
  }, [isCurrentYear, t]);

  const qualityDescription = useMemo(() => {
    if (quality.status === "good") return t("esg.quality_good");
    if (quality.status === "partial") return t("esg.quality_partial");
    if (quality.status === "insufficient") return t("esg.quality_insufficient");
    return "";
  }, [quality.status, t]);

  if (isLoading && !esgData) return <Spinner height="80vh" />;
  if (isError && !esgData)
    return (
      <div className="esg-error-wrapper">
        <ServerError />
        <button className="esg-retry-btn" onClick={() => refetch()}>
          {t("esg.retry") || "Retry"}
        </button>
      </div>
    );

  if (isWithoutProjectScope) {
    return (
      <div className="esg-page">
        <div className="esg-header">
          <Container fluid>
            <div className="esg-header-inner">
              <div className="esg-header-left">
                <h1 className="esg-title">{t("esg.title")}</h1>
                <p className="esg-subtitle">{t("esg.subtitle")}</p>
              </div>
            </div>
          </Container>
        </div>
        <div className="esg-content">
          <Container fluid>
            <div className="esg-without-project-notice">
              <p>{t("esg.without_project_not_supported")}</p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="esg-page">
      {/* Header */}
      <div className="esg-header">
        <Container fluid>
          <div className="esg-header-inner">
            <div className="esg-header-left">
              <h1 className="esg-title">{t("esg.title")}</h1>
              <p className="esg-subtitle">{t("esg.subtitle")}</p>
              <span className="esg-scope">
                {t("esg.reporting_scope")}: {scope.year || year} &bull; {projectLabel} &bull;{" "}
                {areaLabel}
              </span>
            </div>
            <div className="esg-header-controls">
              <button className="esg-control-btn" disabled>
                {t("esg.generate_pdf")}
              </button>
              <button className="esg-control-btn" disabled>
                {t("esg.export_csv")}
              </button>

              <select
                className="esg-select"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                aria-label={t("esg.year")}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>

              <select
                className="esg-select"
                value={areaId || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setAreaId(val ? Number(val) : null);
                }}
                aria-label={t("esg.area")}
                disabled={
                  effectiveProjectId == null ||
                  isAreasLoading ||
                  isAreasError ||
                  projectAreas.length === 0
                }
              >
                <option value="">{t("esg.all_areas")}</option>
                {projectAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>

              {quality.status && (
                <span
                  className={`esg-quality-badge esg-quality-badge--${quality.status || "unknown"}`}
                  title={qualityDescription}
                  aria-label={`${t("esg.data_quality")}: ${
                    quality.coverage_percent != null ? `${quality.coverage_percent}%` : "–"
                  }. ${qualityDescription}`}
                >
                  {t("esg.data_quality")}:{" "}
                  {quality.coverage_percent != null ? `${quality.coverage_percent}%` : "–"}
                </span>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Content */}
      <div className="esg-content">
        <Container fluid>
          {/* KPI Row */}
          <div className="esg-kpi-row">
            <EsgKpiCard
              title="esg.completed_irrigation_volume"
              value={formatNumber(summary.completed_irrigation_volume_liters)}
              unit={t("esg.liters")}
              badge="Measured"
              delta={summary.completed_irrigation_volume_delta_percent}
              deltaLabel={t("esg.vs_last_period")}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              }
            />
            <EsgKpiCard
              title="esg.estimated_water_saved"
              value={formatNumber(summary.estimated_water_saved_m3, {
                maximumFractionDigits: 1,
              })}
              unit="m³"
              badge="Estimated"
              delta={summary.estimated_water_saved_delta_percent}
              deltaLabel={t("esg.vs_last_period")}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                </svg>
              }
            />
            <EsgKpiCard
              title="esg.irrigation_efficiency"
              value={formatNumber(summary.irrigation_efficiency_percent, {
                maximumFractionDigits: 1,
              })}
              unit="%"
              badge="Derived"
              delta={summary.irrigation_efficiency_delta_percent}
              deltaLabel={t("esg.vs_last_period")}
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              }
            />
            <EsgKpiCard
              title="esg.estimated_co2_avoided"
              value={formatNumber(summary.estimated_co2_avoided_kg, {
                maximumFractionDigits: 1,
              })}
              unit="kg CO₂e"
              badge="Estimated"
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 8c.7-1 1-2.2 1-3.5C18 2 16 0 16 0s-2 2-2 4.5c0 1.3.3 2.5 1 3.5" />
                  <path d="M9 12c-2 0-4-1-4-4 0-2 2-4 4-4s4 2 4 4c0 3-2 4-4 4z" />
                  <path d="M12 22c-4 0-8-2-8-6 0-3 3-6 8-6s8 3 8 6c0 4-4 6-8 6z" />
                </svg>
              }
            />
          </div>

          {/* Main content: full-width chart */}
          <div className="esg-main-grid">
            <Card className="esg-panel esg-chart-panel">
              <Card.Header className="esg-panel-header esg-analysis-header">
                <div>
                  <h6 className="mb-0">{t("esg.irrigation_analysis")}</h6>
                  <small className="text-muted">{chartSubtitle}</small>
                </div>
                <button
                  type="button"
                  className="esg-methodology-trigger"
                  onClick={() => setShowMethodology(true)}
                  aria-label={t("esg.methodology")}
                >
                  <svg
                    className="esg-methodology-trigger-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  {t("esg.methodology")}
                </button>
              </Card.Header>
              <Card.Body>
                <EsgResourceEfficiencyChart
                  months={chart.months || []}
                  completedVolume={chart.completed_irrigation_volume_liters || []}
                  baselineVolume={chart.estimated_baseline_irrigation_liters || []}
                  waterSaved={chart.estimated_water_saved_liters || []}
                  loading={isLoading}
                  selectedYear={year}
                />
              </Card.Body>
            </Card>
          </div>

          {/* Disclaimer – translated */}
          <div className="esg-disclaimer">
            <span className="esg-disclaimer-label">{t("esg.disclaimer_title")}</span>
            <p>{t("esg.disclaimer_text")}</p>
          </div>
        </Container>
      </div>

      {/* Methodology Modal */}
      <Modal
        show={showMethodology}
        onHide={() => setShowMethodology(false)}
        size="lg"
        centered
        scrollable
        className="esg-methodology-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("esg.methodology")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Accordion defaultActiveKey="calculation">
            <Accordion.Item eventKey="calculation">
              <Accordion.Header>{t("esg.calculation_basis")}</Accordion.Header>
              <Accordion.Body>
                <ul className="esg-methodology-list">
                  {CALC_BASIS_KEYS.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="assumptions">
              <Accordion.Header>{t("esg.included_assumptions")}</Accordion.Header>
              <Accordion.Body>
                <ul className="esg-methodology-list">
                  {ASSUMPTION_KEYS.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="exclusions">
              <Accordion.Header>{t("esg.exclusions")}</Accordion.Header>
              <Accordion.Body>
                <ul className="esg-methodology-list">
                  {EXCLUSION_KEYS.map((key) => (
                    <li key={key}>
                      {t(key, {
                        count: exclusionCount ?? 0,
                      })}
                    </li>
                  ))}
                </ul>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="details">
              <Accordion.Header>{t("esg.methodology_details")}</Accordion.Header>
              <Accordion.Body>
                <div className="esg-methodology-details">
                  {methodology.methodology_version && (
                    <div className="esg-methodology-detail-row">
                      <span>{t("esg.methodology_version")}</span>
                      <span>{methodology.methodology_version}</span>
                    </div>
                  )}
                  {methodology.updated_label && (
                    <div className="esg-methodology-detail-row">
                      <span>{t("esg.methodology_updated")}</span>
                      <span>
                        {methodology.updated_label === "Updated: first draft"
                          ? t("esg.methodology_first_draft")
                          : methodology.updated_label}
                      </span>
                    </div>
                  )}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="references">
              <Accordion.Header>{t("esg.references")}</Accordion.Header>
              <Accordion.Body>
                <p>{t("esg.references_explanation")}</p>
                <ul className="esg-methodology-list">
                  <li>
                    <a
                      href="https://ghgprotocol.org/corporate-standard"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("esg.ref_corporate_standard")}
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://ghgprotocol.org/scope-2-guidance"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t("esg.ref_scope2_guidance")}
                    </a>
                  </li>
                </ul>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Modal.Body>
      </Modal>
    </div>
  );
}
